import { Injectable, inject, signal } from '@angular/core';

import { Module, School, resolvePricingYear } from '@awo-care/shared-core';
import { isModuleActive } from './module.utils';
import { Firestore, collection, doc, getDoc, getDocs, orderBy, query, where } from 'firebase/firestore';
import { FIREBASE_FIRESTORE } from '@awo-care/shared-firebase';

@Injectable({
  providedIn: 'root',
})
export class SchoolService {
  protected firestore = inject(FIREBASE_FIRESTORE);
  protected schoolsCollection = collection(this.firestore, 'schools');

  protected schoolsSignal = signal<School[]>([]);
  schools = this.schoolsSignal.asReadonly();

  protected schoolDetailsSignal = signal<School | undefined>(undefined);
  schoolDetails = this.schoolDetailsSignal.asReadonly();

  /** Vertrag: enthält ausschließlich aktive Module. */
  protected modulesSignal = signal<Module[]>([]);
  modules = this.modulesSignal.asReadonly();

  /**
   * Alle Module inkl. deaktivierter — nur gefüllt, nachdem mit
   * `loadModulesForSchool(id, true)` geladen wurde (Modul-Verwaltung).
   */
  protected allModulesSignal = signal<Module[]>([]);
  allModules = this.allModulesSignal.asReadonly();

  async getSchool(
    schoolId: string,
    schoolYear?: number
  ): Promise<School | undefined> {
    try {
      // Clear previous school's data immediately to prevent stale data display
      this.schoolDetailsSignal.set(undefined);
      this.modulesSignal.set([]);
      this.allModulesSignal.set([]);

      const schoolRef = doc(this.schoolsCollection, schoolId);
      const schoolDoc = await getDoc(schoolRef);

      if (!schoolDoc.exists()) {
        return undefined;
      }
      const school = { id: schoolDoc.id, ...schoolDoc.data() } as School;

      // Module der Schiene laden, die für das gewünschte Schuljahr gilt (#746).
      // Signal erst nach dem Modul-Load setzen — school-module-change liest
      // school.modules aus dem Signal.
      const pricingYear =
        schoolYear === undefined
          ? undefined
          : (resolvePricingYear(school, schoolYear) ?? undefined);
      const modules = await this.loadModulesForSchool(
        schoolId,
        false,
        pricingYear
      );
      school.modules = modules;
      this.schoolDetailsSignal.set(school);
      return school;
    } catch (error) {
      console.error('Error getting school:', error);
      throw this.handleError(error, 'Error getting school');
    }
  }

  async getSchoolByCostUnit(costUnit: string): Promise<School | undefined> {
    const allSchool = this.schoolsSignal();
    const school = allSchool.find((school) => school.costUnit === costUnit);
    return school;
  }

  /** Liest Schulen, ohne schoolsSignal zu überschreiben (#792). */
  async fetchSchools(showInactive = false): Promise<School[]> {
    const whereConstraint = showInactive
      ? []
      : [where('isActive', '==', true)];

    const queryRef = query(
      this.schoolsCollection,
      ...whereConstraint,
      orderBy('schoolName', 'asc')
    );
    const snapshot = await getDocs(queryRef);

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as School[];
  }

  async loadAllSchools(showInactive = false): Promise<School[]> {
    try {
      const schools = await this.fetchSchools(showInactive);
      this.schoolsSignal.set(schools);
      return schools;
    } catch (error) {
      console.error('Error loading schools:', error);
      throw this.handleError(error, 'Error loading schools');
    }
  }

  protected getModulesCollection(schoolId: string) {
    return collection(this.schoolsCollection, schoolId, 'modules');
  }

  /**
   * Liest Module, ohne die Signale anzufassen — für Module einer anderen Schule
   * als der angezeigten (#792). Die Modulverwaltung nutzt loadModulesForSchool.
   */
  async fetchModules(
    schoolId: string,
    includeInactive = false,
    schoolYear?: number
  ): Promise<Module[]> {
    const modulesCollection = this.getModulesCollection(schoolId);
    const activeConstraint = includeInactive
      ? []
      : [where('isActive', '==', true)];
    // Nicht migrierte Module haben kein schoolYear — ohne Jahr laden wir wie bisher alle.
    const yearConstraint =
      schoolYear === undefined ? [] : [where('schoolYear', '==', schoolYear)];
    const q = query(
      modulesCollection,
      ...yearConstraint,
      ...activeConstraint,
      orderBy('index')
    );
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Module[];
  }

  async loadModulesForSchool(
    schoolId: string,
    includeInactive = false,
    schoolYear?: number
  ): Promise<Module[]> {
    try {
      const modules = await this.fetchModules(
        schoolId,
        includeInactive,
        schoolYear
      );

      if (includeInactive) {
        // Eigenes Signal für die Modul-Verwaltung — modulesSignal behält
        // seinen aktiv-only-Vertrag, parallele Loads können sich nicht
        // gegenseitig überschreiben (#517).
        this.allModulesSignal.set(modules);
        this.modulesSignal.set(modules.filter(isModuleActive));
      } else {
        this.modulesSignal.set(modules);
      }
      return modules;
    } catch (error) {
      console.error('Error loading modules:', error);
      throw this.handleError(error, 'Error loading modules');
    }
  }

  protected handleError(error: unknown, defaultMessage: string): Error {
    if (error instanceof Error) {
      error.message = `${defaultMessage}: ${error.message}`;
      return error;
    }
    return new Error(defaultMessage);
  }

  async loadSchoolsWithModules(showInactive = false): Promise<School[]> {
    try {
      const orderByConstraint = orderBy('schoolName', 'asc');
      const whereConstraint = showInactive
        ? []
        : [where('isActive', '==', true)];
      const queryRef = query(
        this.schoolsCollection,
        ...whereConstraint,
        orderByConstraint
      );
      const snapshot = await getDocs(queryRef);

      const schools = await Promise.all(
        snapshot.docs.map(async (doc) => {
          const school = { id: doc.id, ...doc.data() } as School;
          const modulesCollection = collection(
            this.schoolsCollection,
            doc.id,
            'modules'
          );
          const modulesQuery = query(
            modulesCollection, 
            where('isActive', '==', true),
            orderBy('index')
          );
          const modulesSnapshot = await getDocs(modulesQuery);

          school.modules = modulesSnapshot.docs.map((moduleDoc) => ({
            id: moduleDoc.id,
            ...moduleDoc.data(),
          })) as Module[];

          return school;
        })
      );

      this.schoolsSignal.set(schools);
      return schools;
    } catch (error) {
      console.error('Error loading schools with modules:', error);
      throw this.handleError(error, 'Error loading schools with modules');
    }
  }
}
